// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API functions for retrieving fixed assets rollforward data from the backend.
 * Each response is a full waterfall for a single cost currency.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import {
  type FixedAssetsRollforwardData,
  rollforwardAssetSchema,
} from '@/report/api/types';
import { parseArrayInChunks } from '@/utilities/chunked-parse';
import { DateTime } from 'luxon';
import { z } from 'zod';

// Each phase's units/dollars are validated up front; a phase's assets array (a
// waterfall phase can hold 100k+ rows) is kept out of this schema and validated
// separately in chunks below, so the eager parse never blocks the UI thread.
const phaseMetadataSchema = z.object({
  units: z.number(),
  dollars: z.string(),
});

const fixedAssetsRollforwardMetadataSchema = z.object({
  data: z.object({
    start: phaseMetadataSchema,
    added: phaseMetadataSchema,
    depreciated: phaseMetadataSchema,
    sold: phaseMetadataSchema,
    scrapped: phaseMetadataSchema,
    end: phaseMetadataSchema,
  }),
  metadata: responseMetadataSchema,
});

interface AssetRollforwardParameters {
  startDate: DateTime;
  endDate: DateTime;
  currency: IsoCurrencyCode;
}

/**
 * Retrieves a single cost currency's fixed-asset rollforward waterfall for the
 * specified date range. Validates each phase's units/dollars up front and its
 * assets in chunks to keep the UI responsive on large waterfalls.
 *
 * @param params - The date range and currency parameters
 * @returns Promise resolving to the single-currency fixed-asset rollforward
 */
export async function getFixedAssetsRollforwardData(
  params: AssetRollforwardParameters
): Promise<FixedAssetsRollforwardData> {
  const startDateString = params.startDate.toISODate();
  const endDateString = params.endDate.toISODate();

  const path = '/v1/report/asset/fixed-rollforward';
  // The wire shape equals the parsed shape (the schema has no transforms); it is
  // validated below (envelope + metadata eagerly, assets in chunks).
  const response = await api.get<{ data: FixedAssetsRollforwardData }>(path, {
    params: {
      startDate: startDateString,
      endDate: endDateString,
      currency: params.currency,
    },
  });

  const meta = await parseResponse(
    fixedAssetsRollforwardMetadataSchema,
    response.data,
    { schemaName: 'FixedAssetsRollforwardData', endpoint: path }
  );

  const raw = response.data.data;
  const parsePhase = async (
    metadata: z.infer<typeof phaseMetadataSchema>,
    assets: z.infer<typeof rollforwardAssetSchema>[]
  ) => ({
    ...metadata,
    // Sequential await keeps the yields between chunks effective.
    assets: await parseArrayInChunks(assets, rollforwardAssetSchema, {
      schemaValidation: { schemaName: 'RollforwardAsset', endpoint: path },
    }),
  });

  return {
    start: await parsePhase(meta.data.start, raw.start.assets),
    added: await parsePhase(meta.data.added, raw.added.assets),
    depreciated: await parsePhase(
      meta.data.depreciated,
      raw.depreciated.assets
    ),
    sold: await parsePhase(meta.data.sold, raw.sold.assets),
    scrapped: await parsePhase(meta.data.scrapped, raw.scrapped.assets),
    end: await parsePhase(meta.data.end, raw.end.assets),
  };
}
